import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getDetailOrder } from '../services/orders'

export const getOrderDetail = createAsyncThunk('order/getOrderDetail', async (orderId) => {
  const params = {
    // 'fields[-order]': 'details,show_as_pound',
    'include': 'channels',
    timestamp: `${new Date().getTime()}`
  }
  const response = await getDetailOrder(orderId, params)
  return response
})

const orderDetail = createSlice({
  name: 'order_detail',
  initialState: {
    isLoadingOrder: true,
    attributes: {},
    included: {},
    relationships: {},
    summary: {
      customerName: '',
      invoiceCode: '',
      paymentStatus: '',
      orderStatus: '',
      type: '',
      category: '',
      codProfit: 0,
      billingInformation: '',
      shippingInformation: '',
      totalLogisticCharges: 0,
      totalCustomCharges: 0,
      totalGoodsValue: 0,
      grandTotal: 0,
      payAmount: 0,
      totalHaveBeenPaid: 0,
      remainingBalance: 0,
      additionalOrderStatus: null,
      salesNote: '',
      shortUrl: '',
      soDurationDays: null,
      signature: '',
      showSalesQuote: false,
      invoinceUrl: ''
    },
    general: {
      payCustom: '',
      payViaAli: '',
      showVolumeWeightCharge: '',
      paymentStatus: '',
      orderStatus: '',
      type: '',
      category: '',
      terms: null,
      terms_note: null,
      loadType: null,
      insurenceValue: null,
      freightValue: null,
      billingInformation: {
        first_name: '',
        last_name: '',
        company: '',
        address_1: '',
        address_2: '',
        city: '',
        city_id: '',
        state: '',
        subdistrict: '',
        postcode: '',
        country: '',
        email: '',
        phone: ''
      },
      shippingInformation: {
        first_name: '',
        last_name: '',
        company: '',
        address_1: '',
        address_2: '',
        city: '',
        city_id: '',
        state: '',
        subdistrict: '',
        postcode: '',
        country: '',
        email: '',
        phone: ''
      },
      grandTotal: 0,
      totalPrice: 0,
      orderCurrency: '',
      undername: '',
      showLandedOnly: false,
      landedExcludeVat: false,
      showAsPound: false
    },
    logistics: {
      totalProductOriginalCost: 0,
      totalCbm: 0,
      revisedTotalCbm: 0,
      totalWeight: 0,
      revisedTotalWeight: 0,
      totalProductCost: 0,
      totalCustomCharges: 0,
      revisedTotalCustomCharges: 0,
      totalLogisticCharges: 0,
      revisedTotalLogisticCharges: 0,
      grandTotal: 0,
      payAmount: 0,
    },
    additionalInfo: {
      destination: '',
      markingCode: '',
      origin: '',
      supplierName: '',
    },
    tax: {
      total_duty: 0,
      total_wht: 0,
      total_vat: 0,
      revised_total_duty: null,
      revised_total_vat: null,
      revised_total_wht: null,
    },
    dropship: {
      senderName: '',
      orderNo: '',
      platform: {
        name: '',
        shippingCourier: '',
        shippingDocument: '',
        trackingNumber: ''
      },
      platformId: ''
    },
    showQC: false,
    paymentMethods: []
  },
  reducers: {
    orderDetailState: (state, action) => {
      state.isLoadingOrder = action.payload.isLoadingOrder
      state.attributes = action.payload.attributes
      state.included = action.payload.included
      state.relationships = action.payload.relationships
      state.summary = action.payload.summary
      state.general = action.payload.general
      state.logistic = action.payload.logistic
      state.additionalInfo = action.payload.additionalInfo
      state.tax = action.payload.tax
      state.dropship = action.payload.dropship
      state.showQC = action.payload.showQc
    },
    updateOrderSummary: (state, action) => {
      state.summary = action.payload
    },
    updateOrderGeneral: (state, action) => {
      state.general = action.payload
    },
    updatePaymentMethods: (state, action) => {
      state.paymentMethods = action.payload
    },
    updateOrderStatusQC: (state, action) => {
      state.showQC = action.payload
    }
  },
  extraReducers: {
    [getOrderDetail.pending]: (state) => {
      state.isLoadingOrder = true
    },
    [getOrderDetail.fulfilled]: (state, action) => {
      state.attributes = action.payload.data.attributes
      state.included = action.payload.included
      state.relationships = action.payload.data.relationships
      state.summary = setSummary(action.payload)
      state.general = setGeneral(action.payload.data)
      state.logistic = setLogistic(action.payload)
      state.additionalInfo = setAdditionalInfo(action.payload.data)
      state.paymentMethods = setPaymentMethod(action.payload)
      state.tax = setTax(action.payload)
      state.dropship = setDropship(action.payload.data)
      state.showQC = setStatusQC(action.payload.data)
      state.isLoadingOrder = false
    },
    [getOrderDetail.rejected]: (state) => {
      state.isLoadingOrder = false
    }
  }
})

const handleDataIncluded = (data, key, id) => {
  return data[key][id].attributes
}

const setSummary = (payload) => {
  const attributes = payload.data.attributes
  const relationships = payload.data.relationships
  const included = payload.included

  const getCustomCharge = handleDataIncluded(included, 'customs_charge', relationships.customs_charge.id)

  const generateCustomerNeedToPay = () => {
    if (attributes.order_type === 'forwarding') {
      return attributes.grand_total - attributes.total_goods_value
    }

    if (!attributes.pay_custom) {
      return attributes.grand_total - getCustomCharge.total_custom_charges
    }
    return attributes.grand_total
  }

  const generateLinkSo = () => {
    const apiUrl = process.env.REACT_APP_SERVER_API_URL
    const handleApiUrl = (api) => {
      if (api.includes('api2dev')) {
        return 'https://app-dev.asiacommerce.net'
      } else {
        return 'https://app.asiacommerce.net'
      }
    }
    const base64Format = btoa(`${attributes.id}|${attributes.order_code}|${attributes.user_id}`)
    return `${handleApiUrl(apiUrl)}/order/invoice/${base64Format}`
  }

  const orderAndPaymentStatus = Object.values(included.statuses)
  const remapOrderAndPaymentStatus = orderAndPaymentStatus.map((value) => {
    return {
      id: value.attributes.id,
      type: value.attributes.type,
      name: value.attributes.name,
      date: value.attributes.pivot.date,
    }
  })
  const paymentStatus = remapOrderAndPaymentStatus.filter(val => val.type === 1)[0]
  const orderStatus = remapOrderAndPaymentStatus.filter(val => val.type === 2)[0]

  const mappingSummary = {
    orderId: attributes.id,
    orderCode: attributes.order_code,
    orderDate: attributes.order_date,
    createdBy: attributes.created_by,
    createdAt: attributes.created_at,
    updatedBy: attributes.updated_by,
    updatedAt: attributes.updated_at,
    agentId: attributes.ordered_by_user_id,
    agentName: '',
    excerpt: attributes.excerpt,
    agentCommision: attributes.pending_commision,
    customerName: attributes.customer_name,
    type: attributes.order_type,
    category: attributes.order_category,
    codProfit: attributes.cod_profit,
    goodsCurrency: attributes.currency_origin,
    orderCurrency: attributes.currency_order,
    goodsCurrencyRate: attributes.user_conversion,
    customCurrencyRate: attributes.conversion,
    totalGoodsValue: attributes.total_goods_value,
    totalCustomCharges: getCustomCharge.total_custom_charges,
    totalLogisticCharges: attributes.total_logistic_charges,
    grandTotal: attributes.grand_total,
    customerNeedToPay: generateCustomerNeedToPay(),
    totalHaveBeenPaid: attributes.been_paid,
    remainingBalance: generateCustomerNeedToPay() - attributes.been_paid,
    coupons: attributes.coupons === null ? [] : attributes.coupons,
    totalLogisticChargesBeforeDiscount: attributes.total_logistic_charges_before_discount,
    totalGoodsValueBeforeDiscount: attributes.total_goods_value_before_discount,
    userId: attributes.user_id,
    additionalOrderStatus: attributes.remark_status_details,
    salesNote: attributes.sales_note,
    paymentStatusId: paymentStatus.id,
    paymentStatus: paymentStatus.name,
    paymentStatusDate: paymentStatus.date,
    orderStatusId: orderStatus.id,
    orderStatus: orderStatus.name,
    orderStatusDate: '',
    shortUrl: attributes.short_url,
    soDurationDays: attributes.so_duration_days,
    signature: '',
    showSalesQuote: attributes.is_show_sales_quote,
    invoiceUrl: attributes.invoice_url, // will deprecated soon
    linkSo: generateLinkSo() // its same with invoiceUrl but its generating From FE
  }
  return mappingSummary
}

const setGeneral = (payload) => {
  const attributes = payload.attributes
  const mappingGeneralInfo = {
    payCustom: attributes.pay_custom,
    payViaAli: attributes.pay_via_ali,
    showVolumeWeightCharge: attributes.show_volume_weight_charge,
    showPriceOrigin: attributes.show_price_origin,
    terms: attributes.terms,
    terms_note: attributes.terms_note,
    loadType: attributes.load_type,
    insurenceOriginalValue: attributes.insurance_value,
    insuranceValue: attributes.insurance_value_order,
    freightOriginalValue: attributes.freight_value,
    freightValue: attributes.freight_value_order,
    billingInformation: attributes.order_infos.billing,
    shippingInformation: attributes.order_infos.shipping,
    grandTotal: attributes.grand_total,
    totalPrice: attributes.total_price,
    orderCurrency: attributes.currency_order,
    undername: attributes.undername,
    showLandedOnly: attributes.is_show_landed_only,
    landedExcludeVat: attributes.is_landed_exclude_vat,
    showAsPound: attributes.show_as_pound
  }
  return mappingGeneralInfo
}

const setLogistic = (payload) => {
  const attributes = payload.data.attributes
  const relationships = payload.data.relationships
  const included = payload.included

  // additional logistic
  const additionalLogisticId = relationships.additional_charge.id
  const additionalLogistic = handleDataIncluded(included, 'additional_charge', additionalLogisticId).additional_charge_details
  const remapAdditionalLogistic = additionalLogistic.map((val) => {
    return {
      id: val.attributes.id,
      additionalChargeId: val.attributes.additional_charge_id,
      name: val.attributes.name,
      group: val.attributes.group,
      grandTotal: val.attributes.amount,
      chargeLabel: val.attributes.charge_label.value
    }
  })

  // custom charge
  const customCharge = handleDataIncluded(included, 'customs_charge', relationships.customs_charge.id)
  const mappingLogisticInfo = {
    shipmentMethod: attributes.shipment_method,
    logisticCost: attributes.logistic_cost,
    logisticPerUnit: attributes.logistic_per_unit,
    additionalLogistic: remapAdditionalLogistic,
    totalProductOriginalCost: attributes.total_goods_value,
    totalCbm: attributes.total_cbm,
    revisedTotalCbm: attributes.revised_total_cbm,
    totalWeight: attributes.total_weight,
    revisedTotalWeight: attributes.revised_total_weight,
    totalProductCost: attributes.total_goods_value,
    totalCustomCharges: customCharge.total_customs_charges,
    revisedTotalCustomCharges: customCharge.revised_total_customs_charges,
    totalLogisticCharges: attributes.total_logistic_charges,
    revisedTotalLogisticCharges: attributes.revised_logistic_cost,
    totalLandedCost: 0,
    totalRevisedLandedCost: 0,
    grandTotal: attributes.grand_total,
    payAmount: attributes.pay_amount,
    beenPaid: attributes.been_paid,
    remainingBalance: attributes.remaining_payment,
    additionalLogisticId
  }

  return mappingLogisticInfo
}

const setAdditionalInfo = (payload) => {
  const attributes = payload.attributes
  const mappingAdditionalInfo = {
    origin: attributes.origin,
    origin_city: attributes.origin_city,
    origin_province: attributes.origin_province,
    origin_address: attributes.origin_address,
    destination: attributes.destination,
    supplier_id: attributes.supplier,
    supplier_name: attributes.supplier_name,
    marking_code: attributes.marking_code,
    shipment_from: attributes.shipment_from,
    shipment_to: attributes.shipment_to,
  }
  return mappingAdditionalInfo
}

const setPaymentMethod = (payload) => {
  const includedPaymentChannel = Object.values(payload.included.channels)
  const remapPaymentChannelId = includedPaymentChannel.map((val) => {
    return val.attributes
  })
  return remapPaymentChannelId
}

const setTax = (payload) => {
  const relationships = payload.data.relationships
  const included = payload.included

  const customCharge = handleDataIncluded(included, 'customs_charge', relationships.customs_charge.id)

  const mappingTax = {
    customs_charge_id: customCharge.id,
    total_duty: Number.parseFloat(customCharge.total_duty).toFixed(2),
    total_wht: Number.parseFloat(customCharge.total_wht).toFixed(2),
    total_vat: Number.parseFloat(customCharge.total_vat).toFixed(2),
    revised_total_duty: Number.parseFloat(customCharge.revised_total_duty).toFixed(2),
    revised_total_vat: Number.parseFloat(customCharge.revised_total_vat).toFixed(2),
    revised_total_wht: Number.parseFloat(customCharge.revised_total_wht).toFixed(2),
    total_custom_charges: customCharge.total_customs_charges,
    revised_custom_charges: customCharge.revised_total_customs_charges,
  }

  return mappingTax
}

const setDropship = (payload) => {
  const attributes = payload.attributes

  const generatePlatfrom = () => {
    const tempDataPlatform = {
      name: '',
      shippingCourier: '',
      shippingDocument: '',
      trackingNumber: ''
    }
    if (attributes.order_infos.platform) {
      if (typeof attributes.order_infos.platform === 'object') {
        tempDataPlatform.name = attributes.order_infos.platform.name ? attributes.order_infos.platform.name.toLowerCase() : ''
        tempDataPlatform.shippingCourier =  attributes.order_infos.platform.shipping_courier
        tempDataPlatform.shippingDocument = attributes.order_infos.platform.shipping_document
        tempDataPlatform.trackingNumber = attributes.order_infos.platform.tracking_number
      } else if (typeof attributes.order_infos.platform === 'string') {
        tempDataPlatform.name = attributes.order_infos.platform.toLowerCase()
      }
    }
    
    return tempDataPlatform
  }
  
  const storeName = attributes.order_infos.store_name
  const mappingDropship = {
    senderName: storeName ===  null ? '-' : attributes.order_infos.store_name,
    orderNo:  storeName === null ? '-' : attributes.order_infos.order_number,
    platform: generatePlatfrom(attributes),
    platformId:  storeName === null ? '-' : attributes.platform_id,
  }
  
  return mappingDropship
}

const setStatusQC = (payload) => {
  const attributes = payload.attributes

  const statusQc = attributes.order_infos.show_qc
  return statusQc ? true : false
}

export const { orderDetailState, updateOrderSummary, updateOrderGeneral, updatePaymentMethods } = orderDetail.actions
export default orderDetail.reducer